import { useCallback, useState } from 'react';

import { useToast } from '@vimeo/bokeh';
import { CircleCheckFilled, CircleExclamationFilled } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { ReportModal, Reason, REASON_UNSELECTED } from '@vimeo/report-modal';

import { ReportReasons } from './constants';
import { usePostReport } from '../../hooks/use-post-report';
export interface ReportVideoProps {
  clipId: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmitReport: () => void;
}

export const ReportVideoModal = ({ clipId, isOpen, onClose, onSubmitReport }: ReportVideoProps) => {
  const [reportReason, setReportReason] = useState<Reason>(REASON_UNSELECTED);
  const reportSuccess = translate('Video reported');
  const reportFailure = translate('Video could not be reported');
  const toast = useToast();
  const onFailure = useCallback(
    () =>
      toast({
        title: reportFailure,
        status: 'error',
        icon: <CircleExclamationFilled />,
      }),
    [reportFailure, toast],
  );

  const onSuccess = useCallback(
    () =>
      toast({
        title: reportSuccess,
        status: 'success',
        icon: <CircleCheckFilled />,
      }),
    [reportSuccess, toast],
  );

  const { isPosting, postReport } = usePostReport({ clipId, onFailure, onSuccess });

  const onCloseCleanup = () => {
    // Reset state and close modal
    setReportReason(REASON_UNSELECTED);
    onClose();
  };

  const submitReport = async () => {
    // Post report, reset form, callback
    await postReport(reportReason);
    onCloseCleanup();
    onSubmitReport();
  };

  return (
    <ReportModal
      header={translate('Report this video')}
      cta={translate('Why are you reporting this video?')}
      reasons={ReportReasons}
      isOpen={isOpen}
      isPosting={isPosting}
      reportReason={reportReason}
      setReportReason={setReportReason}
      onCloseCleanup={onCloseCleanup}
      submitReport={submitReport}
    />
  );
};
